import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Clock, MapPin, ArrowRight, Phone, Loader2, Search, Plane, Building2, Waves, Home as HomeIcon } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import SEO from "../components/SEO";
import { routeSlug } from "../utils/slugify";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ALL_TO_EUR = 100;
const formatLek = (n) => `${Math.round(n).toLocaleString("sq-AL")} L`;
const toEur = (n) => `≈ €${Math.round(n / ALL_TO_EUR)}`;

const CATEGORY_META = {
  airport: { Icon: Plane, label: { sq: "Aeroporti Rinas", en: "Tirana Airport", it: "Aeroporto Tirana" } },
  city: { Icon: Building2, label: { sq: "Qytete kryesore", en: "Major Cities", it: "Città Principali" } },
  coast: { Icon: Waves, label: { sq: "Bregdeti", en: "Coast", it: "Costa" } },
  suburb: { Icon: HomeIcon, label: { sq: "Fshatra & Përreth Lushnjes", en: "Villages around Lushnje", it: "Villaggi vicino Lushnje" } },
};

const Pricing = () => {
  const { t, lang } = useLang();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");

  useEffect(() => {
    axios.get(`${API}/pricing`).then((res) => setItems(res.data || [])).catch(() => setItems([])).finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    let list = items;
    if (activeCategory !== "all") list = list.filter((p) => p.category === activeCategory);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((p) => p.route_to.toLowerCase().includes(q) || p.route_from.toLowerCase().includes(q));
    }
    return list;
  }, [items, search, activeCategory]);

  const categoryOrder = ["airport", "city", "coast", "suburb"];
  const grouped = useMemo(() => {
    const map = {};
    filtered.forEach((p) => { const cat = p.category || "city"; if (!map[cat]) map[cat] = []; map[cat].push(p); });
    return map;
  }, [filtered]);

  const tabs = [
    { id: "all", label: { sq: t.common.all, en: t.common.all, it: t.common.all } },
    { id: "airport", label: CATEGORY_META.airport.label },
    { id: "city", label: CATEGORY_META.city.label },
    { id: "coast", label: CATEGORY_META.coast.label },
    { id: "suburb", label: CATEGORY_META.suburb.label },
  ];

  return (
    <>
      <SEO
        title={t.seo.pricing.title}
        description={t.seo.pricing.description}
        keywords={t.seo.pricing.keywords}
        path="/pricing"
      />

      {/* HERO */}
      <section className="relative pt-32 pb-12 md:pt-40 md:pb-16 bg-cream overflow-hidden">
        <div className="absolute top-10 right-10 w-40 h-40 checkered-fine opacity-20 pointer-events-none" />
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 anim-fade-up relative">
          <h1 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-zinc-900 max-w-4xl leading-[1.1]">
            <span className="font-serif italic font-normal text-zinc-500">{t.accents.noSurprises}</span>{" "}
            {t.pricing.title}
          </h1>
          <p className="mt-5 text-zinc-600 text-base md:text-lg max-w-2xl leading-relaxed">{t.pricing.subtitle}</p>

          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-3 max-w-4xl">
            {[{ to: "Tiranë", price: 4000 }, { to: "Rinas", price: 3500 }, { to: "Sarandë", price: 9000 }, { to: "Ksamil", price: 10000 }].map((r, i) => (
              <Link
                key={i}
                to={`/${routeSlug(r.to)}`}
                data-testid={`pricing-quick-${i}`}
                className="bg-white border border-zinc-200 rounded-2xl p-4 shadow-taxi-card hover:-translate-y-1 hover:shadow-taxi-hover transition-all block"
              >
                <div className="text-zinc-500 text-[10px] uppercase tracking-widest font-semibold">Lushnje →</div>
                <div className="font-display text-base md:text-lg font-semibold text-zinc-900">{r.to}</div>
                <div className="font-display text-xl md:text-2xl font-bold text-zinc-900 mt-1 tracking-tight">{formatLek(r.price)}</div>
                <div className="text-zinc-400 text-xs">{toEur(r.price)}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FILTERS */}
      <section className="bg-white py-5 border-y border-zinc-200 sticky top-24 md:top-28 z-30 backdrop-blur-xl bg-white/90">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 flex flex-col md:flex-row gap-4 items-stretch md:items-center">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
            <input
              data-testid="pricing-search-input" type="text" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder={t.common.searchDestination}
              className="bt-input pl-10"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                data-testid={`pricing-tab-${tab.id}`}
                onClick={() => setActiveCategory(tab.id)}
                className={`px-4 py-2 text-xs font-semibold rounded-full border transition-colors ${
                  activeCategory === tab.id
                    ? "bg-zinc-900 text-[#FFD500] border-zinc-900"
                    : "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-900"
                }`}
              >
                {tab.label[lang] || tab.label.sq}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-mist py-12">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          {loading ? (
            <div data-testid="pricing-loading" className="text-zinc-600 flex items-center gap-3 py-10">
              <Loader2 className="h-5 w-5 animate-spin" /> {t.pricing.empty}
            </div>
          ) : filtered.length === 0 ? (
            <p data-testid="pricing-empty" className="text-zinc-500 py-10 text-center">
              {t.common.noResults}
            </p>
          ) : (
            <div className="space-y-10">
              {categoryOrder.map((cat) => {
                const rows = grouped[cat];
                if (!rows || rows.length === 0) return null;
                const meta = CATEGORY_META[cat];
                const Icon = meta?.Icon || MapPin;
                return (
                  <div key={cat} data-testid={`pricing-category-${cat}`}>
                    <div className="flex items-center gap-3 mb-5">
                      <div className="h-9 w-9 rounded-full bg-[#FFD500] flex items-center justify-center">
                        <Icon className="h-4.5 w-4.5 text-zinc-900" strokeWidth={2.3} />
                      </div>
                      <h2 className="font-display text-xl md:text-2xl font-semibold text-zinc-900 tracking-tight">
                        {meta?.label[lang] || cat}
                      </h2>
                      <span className="text-zinc-500 text-sm">({rows.length})</span>
                    </div>

                    <div className="bg-white border border-zinc-200 rounded-3xl overflow-hidden shadow-taxi-card">
                      <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 bg-zinc-900 text-xs uppercase tracking-[0.15em] font-semibold text-[#FFD500]">
                        <div className="col-span-4">{t.pricing.headerTo}</div>
                        <div className="col-span-2">{t.pricing.headerDuration}</div>
                        <div className="col-span-2">{t.pricing.headerDistance}</div>
                        <div className="col-span-2 text-right">{t.common.waitingPerHour}</div>
                        <div className="col-span-2 text-right">{t.pricing.headerPrice}</div>
                      </div>

                      {rows.map((p, i) => (
                        <Link
                          key={p.id}
                          to={`/${routeSlug(p.route_to)}`}
                          data-testid={`pricing-row-${cat}-${i}`}
                          className="grid grid-cols-2 md:grid-cols-12 gap-3 md:gap-4 px-6 py-4 border-b border-zinc-100 hover:bg-cream transition-colors items-center last:border-b-0 group"
                        >
                          <div className="col-span-2 md:col-span-4 flex items-center gap-2">
                            <MapPin className="h-4 w-4 text-[#FFD500] flex-shrink-0" />
                            <span className="text-zinc-900 font-medium group-hover:underline decoration-[#FFD500] decoration-2 underline-offset-4">{p.route_from} → {p.route_to}</span>
                          </div>
                          <div className="col-span-1 md:col-span-2 flex items-center gap-2 text-zinc-500 text-sm">
                            {p.duration_min ? (<><Clock className="h-3.5 w-3.5" /> {p.duration_min} min</>) : "—"}
                          </div>
                          <div className="col-span-1 md:col-span-2 text-zinc-500 text-sm">
                            {p.distance_km ? `${p.distance_km} km` : "—"}
                          </div>
                          <div className="col-span-1 md:col-span-2 md:text-right text-zinc-500 text-sm">
                            {p.waiting_per_hour_all ? `${Math.round(p.waiting_per_hour_all).toLocaleString("sq-AL")} L` : "—"}
                          </div>
                          <div className="col-span-2 md:col-span-2 text-right">
                            <div className="font-display text-lg md:text-xl font-semibold text-zinc-900 tracking-tight leading-none">
                              {formatLek(p.price_all)}
                            </div>
                            <div className="text-zinc-400 text-xs mt-0.5">{toEur(p.price_all)}</div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <p className="mt-8 text-zinc-500 text-sm italic max-w-3xl">{t.pricing.note}</p>
        </div>
      </section>

      <section className="py-20 bg-paper">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 text-center">
          <h3 className="font-display text-2xl md:text-3xl lg:text-4xl font-semibold text-zinc-900 mb-6 tracking-tight">
            {t.home.ctaBlockTitle}
          </h3>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/book" className="bt-btn-primary">{t.common.bookTaxi} <ArrowRight className="h-4 w-4" /></Link>
            <a href="tel:+355682222286" className="bt-btn-outline"><Phone className="h-4 w-4" /> +355 68 22 22 286</a>
          </div>
        </div>
      </section>
    </>
  );
};

export default Pricing;
